/**
 * Portal backend data source.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  THIS IS THE ONLY FILE THAT CHANGES WHEN THE REAL PORTAL API IS KNOWN │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * Two data sources, selected by DATA_SOURCE below:
 *
 *   'mock'  → deterministic in-browser data (default). The whole dashboard is
 *             fully functional with no backend at all.
 *   'live'  → real agw-config-api `/admin/*` endpoints (read-only, gated by
 *             Cloudflare Access — the engineer's own browser session
 *             authenticates them). See ./portalApi.ts for the concrete
 *             endpoints, response shapes, and type mapping.
 *
 * To go live: set DATA_SOURCE = 'live', point PORTAL_API_CONFIG.baseUrl at the
 * right environment, and fill PORTAL_API_CONFIG.accounts with the account
 * tag/id pairs to monitor (each becomes a row/customer in the matrix). The
 * function signatures and return types never change — everything downstream
 * depends on these shapes.
 *
 * NOTE: the admin API is per-account; there is no fleet-wide "list all
 * accounts" endpoint, so the cross-customer matrix is built by iterating the
 * configured accounts. Cross-origin calls from localhost need the config-api
 * to allow the dev origin via CORS (or a dev proxy) — see README.
 */

import type {
  PortalAccount,
  PortalServer,
  ConnectionStatus,
} from '../../../shared/types'
import {
  getAllServersLive,
  getAllAccountsLive,
  getAllConnectionsLive,
  type PortalApiConfig,
} from './portalApi'

/** A raw connection record as the portal backend is expected to return it. */
export interface PortalConnectionRecord {
  account_id: string
  server_id: string
  connection_status: ConnectionStatus
  is_blocked: boolean
  issue_count: number
  last_seen: string | null
}

// ────────────────────────────────────────────────────────────────────────
// Data-source configuration.
// ────────────────────────────────────────────────────────────────────────

/** Flip to 'live' to use the real agw-config-api admin endpoints. */
const DATA_SOURCE: 'mock' | 'live' =
  (import.meta.env?.VITE_DATA_SOURCE as 'mock' | 'live') ?? 'mock'

/**
 * Live-mode config. baseUrl can be overridden with VITE_PORTAL_API_BASE.
 * Fill `accounts` with the { account_tag, account_id, account_name } tuples
 * the team wants to monitor. Empty by default so a stray 'live' flip can't
 * silently hit prod with no accounts.
 */
const PORTAL_API_CONFIG: PortalApiConfig = {
  baseUrl:
    (import.meta.env?.VITE_PORTAL_API_BASE as string) ??
    'https://config-production.agw.ai.cfdata.org',
  accounts: [
    // { account_tag: '...', account_id: '80965939', account_name: 'Cloudflare (internal)' },
  ],
}

// ────────────────────────────────────────────────────────────────────────
// Public interface — keep these signatures stable.
// ────────────────────────────────────────────────────────────────────────

export async function getAllServers(): Promise<PortalServer[]> {
  if (DATA_SOURCE === 'live') return getAllServersLive(PORTAL_API_CONFIG)
  return MOCK_SERVERS
}

export async function getAllAccounts(): Promise<PortalAccount[]> {
  if (DATA_SOURCE === 'live') return getAllAccountsLive(PORTAL_API_CONFIG)
  return MOCK_ACCOUNTS
}

export async function getAllConnections(): Promise<PortalConnectionRecord[]> {
  if (DATA_SOURCE === 'live') return getAllConnectionsLive(PORTAL_API_CONFIG)
  return MOCK_CONNECTIONS
}

export async function getConnectionsForAccount(
  accountId: string
): Promise<PortalConnectionRecord[]> {
  const all = await getAllConnections()
  return all.filter((c) => c.account_id === accountId)
}

export async function getConnectionsForServer(
  serverId: string
): Promise<PortalConnectionRecord[]> {
  const all = await getAllConnections()
  return all.filter((c) => c.server_id === serverId)
}

// ────────────────────────────────────────────────────────────────────────
// Mock data — deterministic so the UI is stable between reloads.
// Delete this block once the real portal API is wired above.
// ────────────────────────────────────────────────────────────────────────

const MOCK_SERVERS: PortalServer[] = [
  { server_id: 'srv-slack', server_name: 'Slack MCP', provider: 'Slack', base_url: 'https://api.slack.com', description: 'Slack workspace integration' },
  { server_id: 'srv-github', server_name: 'GitHub MCP', provider: 'GitHub', base_url: 'https://api.github.com', description: 'GitHub repository integration' },
  { server_id: 'srv-google', server_name: 'Google Cloud MCP', provider: 'Google', base_url: 'https://www.googleapis.com', description: 'Google Cloud Platform integration' },
  { server_id: 'srv-teams', server_name: 'Microsoft Teams MCP', provider: 'Microsoft', base_url: 'https://graph.microsoft.com', description: 'Microsoft Teams integration' },
  { server_id: 'srv-jira', server_name: 'Jira Cloud MCP', provider: 'Atlassian', base_url: 'https://api.atlassian.com', description: 'Jira Cloud integration' },
  { server_id: 'srv-notion', server_name: 'Notion MCP', provider: 'Notion', base_url: 'https://api.notion.com', description: 'Notion workspace integration' },
  { server_id: 'srv-salesforce', server_name: 'Salesforce MCP', provider: 'Salesforce', base_url: 'https://api.salesforce.com', description: 'Salesforce CRM integration' },
  { server_id: 'srv-stripe', server_name: 'Stripe MCP', provider: 'Stripe', base_url: 'https://api.stripe.com', description: 'Stripe payments integration' },
]

// 32-char hex account IDs (Cloudflare format).
const MOCK_ACCOUNTS: PortalAccount[] = [
  { account_id: '023e105f4ecef8ad9ca31a8372d0c353', account_name: 'TechCorp', contact_email: 'admin@techcorp.com', is_active: true },
  { account_id: '1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d', account_name: 'DataSys Inc', contact_email: 'contact@datasys.com', is_active: true },
  { account_id: 'f9e8d7c6b5a4938271605f4e3d2c1b0a', account_name: 'CloudFirst Ltd', contact_email: 'ops@cloudfirst.com', is_active: true },
  { account_id: '7c6d5e4f3a2b1c0d9e8f7a6b5c4d3e2f', account_name: 'SecureWeb', contact_email: 'admin@secureweb.com', is_active: true },
  { account_id: '3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d', account_name: 'Acme Corp', contact_email: 'it@acme.com', is_active: true },
  { account_id: '9f8e7d6c5b4a39281706f5e4d3c2b1a0', account_name: 'Globex', contact_email: 'ops@globex.com', is_active: true },
  { account_id: '2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a', account_name: 'Initech', contact_email: 'support@initech.com', is_active: true },
  { account_id: '8b7a6c5d4e3f2a1b0c9d8e7f6a5b4c3d', account_name: 'Umbrella LLC', contact_email: 'admin@umbrella.com', is_active: true },
  { account_id: '4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c', account_name: 'Hooli', contact_email: 'eng@hooli.com', is_active: true },
  { account_id: 'c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8', account_name: 'Vandelay Industries', contact_email: 'ops@vandelay.com', is_active: true },
]

/**
 * Deterministic pseudo-random generator so the mock matrix looks realistic
 * but does not change on every request (stable between reloads).
 */
function seeded(a: string, b: string): number {
  let h = 2166136261
  for (const ch of a + '|' + b) {
    h ^= ch.charCodeAt(0)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) / 4294967295
}

function buildMockConnections(): PortalConnectionRecord[] {
  const rows: PortalConnectionRecord[] = []
  const now = Date.now()
  for (const acct of MOCK_ACCOUNTS) {
    for (const srv of MOCK_SERVERS) {
      const r = seeded(acct.account_id, srv.server_id)
      // ~55% of pairs have a connection; the rest are absent (no_data).
      if (r < 0.45) continue

      let status: ConnectionStatus = 'active'
      let is_blocked = false
      let issue_count = 0

      if (r > 0.93) {
        status = 'error'
        is_blocked = true
        issue_count = 2 + Math.floor(r * 6)
      } else if (r > 0.85) {
        status = 'error'
        issue_count = 1 + Math.floor(r * 4)
      } else if (r > 0.78) {
        status = 'inactive'
        issue_count = 0
      }

      rows.push({
        account_id: acct.account_id,
        server_id: srv.server_id,
        connection_status: status,
        is_blocked,
        issue_count,
        last_seen: new Date(now - Math.floor(r * 86400000)).toISOString(),
      })
    }
  }
  return rows
}

const MOCK_CONNECTIONS: PortalConnectionRecord[] = buildMockConnections()
